import { Controller } from '@hotwired/stimulus';
import { useClickOutside } from 'stimulus-use';

export default class extends Controller {
  static targets = ['button', 'menu'];
  static values = {
    open: false
  };

  connect() {
    useClickOutside(this);
    this.toggleFocusInside(false);
    this.tableWrapper = this.element.closest('.staff-table-wrapper');
    this.boundCloseOnScroll = this.closeOnScroll.bind(this);

    this.buttonTarget.addEventListener('click', this.toggleMenu);
  }

  disconnect() {
    this.removeScrollListener();
    this.buttonTarget.removeEventListener('click', this.toggleMenu);
  }

  toggleMenu = () => {
    this.setMenuOpen(!this.openValue);
  };

  clickOutside() {
    this.setMenuOpen(false);
  }

  close(event) {
    event.preventDefault();
    this.setMenuOpen(false);
  }

  setMenuOpen(open) {
    this.openValue = open;

    if (open) {
      this.menuTarget.classList.remove('visually-hidden');
      this.positionMenuIfNeeded();
      this.addScrollListener();
      this.element.addEventListener('keydown', this.closeViaEsc);
    } else {
      this.menuTarget.classList.add('visually-hidden');
      this.resetMenuPosition();
      this.removeScrollListener();
      this.element.removeEventListener('keydown', this.closeViaEsc);
    }

    this.buttonTarget.setAttribute('aria-expanded', JSON.stringify(open));
    this.toggleFocusInside(open);
  }

  closeViaEsc = (event) => {
    if (event.keyCode === 27) {
      this.setMenuOpen(false);
    }
  };

  closeOnScroll() {
    this.setMenuOpen(false);
  }

  addScrollListener() {
    if (!this.tableWrapper) return;

    this.scrollParents = this.getScrollParents(this.buttonTarget);
    this.scrollParents.forEach((parent) => {
      parent.addEventListener('scroll', this.boundCloseOnScroll, { passive: true });
    });
    window.addEventListener('resize', this.boundCloseOnScroll, { passive: true });
  }

  removeScrollListener() {
    if (!this.scrollParents) return;

    this.scrollParents.forEach((parent) => {
      parent.removeEventListener('scroll', this.boundCloseOnScroll);
    });
    window.removeEventListener('resize', this.boundCloseOnScroll);
    this.scrollParents = null;
  }

  getScrollParents(element) {
    const parents = [];
    let parent = element.parentElement;

    while (parent) {
      const { overflow, overflowX, overflowY } = getComputedStyle(parent);

      if ([overflow, overflowX, overflowY].some((value) => ['auto', 'scroll', 'overlay'].includes(value))) {
        parents.push(parent);
      }

      parent = parent.parentElement;
    }

    parents.push(window);

    return parents;
  }

  positionMenuIfNeeded() {
    if (!this.tableWrapper) return;

    const buttonRect = this.buttonTarget.getBoundingClientRect();

    this.menuTarget.style.top = `${buttonRect.bottom + 4}px`;
    this.menuTarget.style.right = `${window.innerWidth - buttonRect.right}px`;
    this.menuTarget.style.left = 'auto';
  }

  resetMenuPosition() {
    if (!this.tableWrapper) return;

    this.menuTarget.style.top = '';
    this.menuTarget.style.right = '';
    this.menuTarget.style.left = '';
  }

  toggleFocusInside(isFocusable) {
    const focusableChildren = this.menuTarget.querySelectorAll(
      'a, button, input, select, textarea'
    );

    focusableChildren.forEach((child) => {
      child.setAttribute('tabindex', isFocusable ? '0' : '-1');
    });
  }
}
